/**
 * Error Detection Service for GenFlappy
 *
 * Centralized error detection, classification, and logging for all error types
 * Story 6.1: Error Detection System
 * Story 8.1: Sentry Integration
 *
 * @module services/errorDetectionService
 */

import * as Sentry from '@sentry/react';
import type { SeverityLevel } from '@sentry/react';
import type { ErrorContext, DemoModeState, AssetType } from '../types/errors';

const CLIENT_TIMEOUT_MS = 45000;
const QUALITY_THRESHOLD = 0.5; // Based on qualityValidation.ts

/**
 * Detect network failure from error
 *
 * @param {Error} error - Error to check
 * @returns {ErrorContext | null} Error context if network failure detected
 */
export function detectNetworkFailure(error: Error): ErrorContext | null {
  // Check for common network error patterns
  const isNetworkError =
    error instanceof TypeError &&
    (error.message.includes('Failed to fetch') ||
      error.message.includes('Network request failed') ||
      error.message.includes('NetworkError'));

  if (!isNetworkError) {
    return null;
  }

  return {
    errorType: 'network',
    timestamp: Date.now(),
    message: `Network failure: ${error.message}`,
    stackTrace: process.env.NODE_ENV === 'development' ? error.stack : undefined,
    severity: 'error',
    demoModeActivated: 'none', // Will be updated by caller
  };
}

/**
 * Detect API failure from error or response
 *
 * @param {Error | Response} errorOrResponse - Error from API call or HTTP response to check
 * @returns {ErrorContext | null} Error context if API failure detected
 */
export function detectAPIFailure(errorOrResponse: Error | Response): ErrorContext | null {
  // Handle Response objects
  if (errorOrResponse instanceof Response) {
    // Successful responses
    if (errorOrResponse.ok) {
      return null;
    }

    // Determine severity based on status code
    let severity: 'warning' | 'error' | 'critical' = 'error';
    if (errorOrResponse.status === 429) {
      severity = 'warning'; // Rate limit - expected behavior
    } else if (errorOrResponse.status >= 500) {
      severity = 'error'; // Server error
    } else if (errorOrResponse.status >= 400) {
      severity = 'warning'; // Client error
    }

    return {
      errorType: 'api',
      timestamp: Date.now(),
      message: `API error: ${errorOrResponse.status} ${errorOrResponse.statusText}`,
      httpStatus: errorOrResponse.status,
      severity,
      demoModeActivated: 'none',
    };
  }

  // Handle Error objects from API calls
  const error = errorOrResponse;
  const errorMessage = error.message.toLowerCase();

  // Check for API-related error patterns
  const isAPIError =
    errorMessage.includes('api') ||
    errorMessage.includes('replicate') ||
    errorMessage.includes('server error') ||
    errorMessage.includes('rate limit') ||
    errorMessage.includes('quota') ||
    /\b(4|5)\d{2}\b/.test(errorMessage); // HTTP status codes 4xx or 5xx

  if (!isAPIError) {
    return null;
  }

  // Extract HTTP status if present in message
  const statusMatch = errorMessage.match(/\b([45]\d{2})\b/);
  const httpStatus = statusMatch ? parseInt(statusMatch[1]) : undefined;

  // Determine severity
  let severity: 'warning' | 'error' | 'critical' = 'error';
  if (httpStatus === 429 || errorMessage.includes('rate limit')) {
    severity = 'warning';
  } else if (httpStatus && httpStatus >= 500) {
    severity = 'error';
  }

  return {
    errorType: 'api',
    timestamp: Date.now(),
    message: `API failure: ${error.message}`,
    httpStatus,
    severity,
    stackTrace: process.env.NODE_ENV === 'development' ? error.stack : undefined,
    demoModeActivated: 'none',
  };
}

/**
 * Detect asset load failure
 *
 * @param {Error} error - Error from asset loading
 * @param {string} assetType - Type of asset that failed
 * @returns {ErrorContext | null} Error context if asset load failure detected
 */
export function detectAssetLoadFailure(error: Error, assetType: string): ErrorContext | null {
  return {
    errorType: 'asset_load',
    timestamp: Date.now(),
    message: `Asset load failure for ${assetType}: ${error.message}`,
    assetType: assetType as AssetType,
    stackTrace: process.env.NODE_ENV === 'development' ? error.stack : undefined,
    severity: 'warning',
    demoModeActivated: 'none',
  };
}

/**
 * Detect timeout failure
 *
 * @param {number} elapsedMs - Elapsed time in milliseconds
 * @returns {ErrorContext | null} Error context if timeout detected
 */
export function detectTimeoutFailure(elapsedMs: number): ErrorContext | null {
  if (elapsedMs <= CLIENT_TIMEOUT_MS) {
    return null;
  }

  return {
    errorType: 'timeout',
    timestamp: Date.now(),
    message: `Operation timed out after ${elapsedMs}ms (threshold: ${CLIENT_TIMEOUT_MS}ms)`,
    severity: 'warning',
    demoModeActivated: 'none',
  };
}

/**
 * Detect quota exceeded error
 *
 * @param {Error} error - Error to check
 * @returns {ErrorContext | null} Error context if quota exceeded detected
 */
export function detectQuotaExceeded(error: Error): ErrorContext | null {
  const isQuotaError =
    error instanceof DOMException &&
    (error.name === 'QuotaExceededError' || error.message.includes('quota'));

  if (!isQuotaError) {
    return null;
  }

  return {
    errorType: 'quota',
    timestamp: Date.now(),
    message: 'Storage quota exceeded',
    severity: 'warning',
    demoModeActivated: 'none',
  };
}

/**
 * Detect quality validation failure
 *
 * @param {string} assetType - Type of asset
 * @param {number} score - Quality score (0-1)
 * @returns {ErrorContext | null} Error context if quality failure detected
 */
export function detectQualityFailure(assetType: string, score: number): ErrorContext | null {
  if (score >= QUALITY_THRESHOLD) {
    return null;
  }

  return {
    errorType: 'quality',
    timestamp: Date.now(),
    message: `Quality validation failed for ${assetType}: score ${score.toFixed(2)} below threshold ${QUALITY_THRESHOLD}`,
    assetType: assetType as AssetType,
    severity: 'info',
    demoModeActivated: 'none',
  };
}

/**
 * Classify demo mode based on failed assets
 *
 * @param {string[]} failedAssets - List of failed asset types
 * @returns {'none' | 'partial' | 'full'} Demo mode classification
 */
export function classifyDemoMode(failedAssets: string[]): 'none' | 'partial' | 'full' {
  if (failedAssets.length === 0) {
    return 'none';
  }
  if (failedAssets.length === 1) {
    return 'partial';
  }
  return 'full';
}

/**
 * Update demo mode state when asset fails or recovers
 *
 * @param {DemoModeState} state - Current state
 * @param {AssetType} assetType - Asset that changed
 * @param {boolean} failed - True if asset failed, false if recovered
 * @returns {DemoModeState} Updated state
 */
export function updateDemoModeState(
  state: DemoModeState,
  assetType: AssetType,
  failed: boolean
): DemoModeState {
  const failedAssets = failed
    ? [...state.failedAssets, assetType]
    : state.failedAssets.filter((a) => a !== assetType);

  const mode = classifyDemoMode(failedAssets);

  return {
    failedAssets,
    mode,
    timestamp: Date.now(),
  };
}

/**
 * Detect JavaScript runtime error
 *
 * @param {Error | string} error - Error object or error message
 * @returns {ErrorContext} Error context for JavaScript errors
 */
export function detectJavaScriptError(error: Error | string): ErrorContext {
  const errorObj = typeof error === 'string' ? new Error(error) : error;

  return {
    errorType: 'javascript',
    timestamp: Date.now(),
    message: errorObj.message || String(error),
    stackTrace: process.env.NODE_ENV === 'development' ? errorObj.stack : undefined,
    severity: 'error',
    demoModeActivated: 'none',
  };
}

/**
 * Sanitize user input to remove PII
 *
 * @param {string} input - User input to sanitize
 * @returns {string} Sanitized input
 */
export function sanitizeUserInput(input: string): string {
  let sanitized = input;

  // Redact email addresses
  sanitized = sanitized.replace(
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
    '[REDACTED]'
  );

  // Redact URLs
  sanitized = sanitized.replace(/https?:\/\/[^\s]+/g, '[REDACTED]');

  // Truncate to 100 chars
  if (sanitized.length > 100) {
    sanitized = sanitized.substring(0, 100) + '...';
  }

  return sanitized;
}

/**
 * Sanitize error context to remove sensitive information
 *
 * @param {ErrorContext} context - Error context to sanitize
 * @returns {ErrorContext} Sanitized error context
 */
export function sanitizeErrorContext(context: ErrorContext): ErrorContext {
  const sanitized = { ...context };

  // Sanitize message - remove API keys and URLs
  sanitized.message = context.message
    .replace(/REPLICATE_API_KEY_[a-zA-Z0-9]+/g, '[REDACTED]')
    .replace(/sk-[a-zA-Z0-9]+/g, '[REDACTED]')
    .replace(/https?:\/\/[^\s]+/g, '[REDACTED_URL]');

  // Truncate stack trace to 1000 chars
  if (sanitized.stackTrace && sanitized.stackTrace.length > 1000) {
    sanitized.stackTrace = sanitized.stackTrace.substring(0, 1000) + '...';
  }

  // Sanitize user inputs if present
  if (sanitized.userInputs) {
    sanitized.userInputs = {
      character: sanitizeUserInput(sanitized.userInputs.character),
      obstacle: sanitizeUserInput(sanitized.userInputs.obstacle),
      background: sanitizeUserInput(sanitized.userInputs.background),
    };
  }

  return sanitized;
}

/**
 * Map ErrorContext severity to Sentry severity level
 * Story 8.1 Task 2.2
 *
 * @param {string} severity - ErrorContext severity
 * @returns {SeverityLevel} Sentry severity level
 */
export function mapSeverityToSentryLevel(severity: string): SeverityLevel {
  switch (severity) {
    case 'critical':
      return 'fatal';
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'error';
  }
}

/**
 * Add breadcrumb for user action tracking
 * Story 8.1 Task 2.3
 *
 * @param {string} category - Breadcrumb category (e.g., 'generation', 'cache', 'game')
 * @param {string} message - Description of the action
 * @param {Record<string, unknown>} data - Optional additional data
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    category,
    message,
    level: 'info',
    data,
  });
}

/**
 * Log error to console and Sentry
 * Story 8.1 Task 2.1: Integrated Sentry error reporting
 *
 * @param {ErrorContext} context - Error context to log
 */
export function logError(context: ErrorContext): void {
  // Sanitize before logging (Task 2.4 - ensures PII is removed)
  const sanitized = sanitizeErrorContext(context);

  if (process.env.NODE_ENV === 'development') {
    console.error('[ErrorDetection]', sanitized);
  } else {
    // Production: only log essential info
    console.error('[Error]', {
      type: sanitized.errorType,
      severity: sanitized.severity,
      timestamp: sanitized.timestamp,
    });
  }

  // Story 8.1: Sentry integration
  const sentryLevel = mapSeverityToSentryLevel(sanitized.severity);

  if (sanitized.errorType === 'quality') {
    // Quality failures are info-level messages, not exceptions (AC #4)
    Sentry.captureMessage(sanitized.message, sentryLevel);
  } else {
    // All other errors are captured as exceptions (AC #2, #3)
    Sentry.captureException(new Error(sanitized.message), {
      level: sentryLevel,
      tags: {
        errorType: sanitized.errorType,
        demoModeActivated: sanitized.demoModeActivated,
      },
      extra: {
        timestamp: sanitized.timestamp,
        assetType: sanitized.assetType,
        httpStatus: sanitized.httpStatus,
      },
    });
  }
}
